import { useContext, useRef } from "react";
import classNames from "classnames";
import { motion } from "framer-motion";

import { ImageContext } from "../../../../components";
import { PaginationButton } from "../../../../components/CardCarousel/CardCarousel";
import { useScrollHandler } from "../../../../components/CardCarousel/useScrollHandler";
import useCustomScrollbar from "../../../../hooks/useCustomScrollbar";
import {
  findFirstMatchingSchematic,
  VariantSchematic,
} from "../../../util/variant-schematics";
import { RichText } from "../..";
import selectors from "../selectors";

interface Props {
  selectedOptions: Record<string, string>;
  schematics: VariantSchematic[];
}

function SchematicsComponent({
  matchingSchematic,
}: {
  matchingSchematic: VariantSchematic;
}) {
  const ImageElement = useContext(ImageContext);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const {
    x,
    thumbWidth,
    shouldScroll: canScroll,
  } = useCustomScrollbar(scrollContainerRef, scrollTrackRef);

  const { onPrevClick, onNextClick, isAtBeginningOfScroll, isAtEndOfScroll } =
    useScrollHandler({
      scrollRef: scrollContainerRef,
      numberOfChildren: matchingSchematic?.images?.length || 0,
      carouselName: "Dimensions",
    });

  const shouldScroll =
    canScroll &&
    matchingSchematic.images &&
    matchingSchematic.images.length > 1;

  return (
    <div
      data-testid={selectors.schematics.container}
      className="flex flex-col gap-4"
    >
      {matchingSchematic.images && (
        <div
          className="flex flex-col gap-4"
          data-testid={selectors.schematics.imageContainer}
        >
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto snap-x snap-mandatory whitespace-nowrap no-scrollbar"
          >
            <div className="flex">
              {matchingSchematic.images.map((image) => (
                <div
                  key={image.src}
                  className="snap-start relative w-full aspect-[21/9] shrink-0"
                >
                  <ImageElement src={image.src} alt={image.alt} />
                </div>
              ))}
            </div>
          </div>

          {/* scroll track and arrows */}
          <div
            data-testid={selectors.schematics.scrollTrack}
            className={classNames(
              "flex px-6 items-center justify-center gap-8",
              {
                hidden: !shouldScroll,
              }
            )}
          >
            <div ref={scrollTrackRef} className="flex-1 h-0.5 bg-lightgray">
              <motion.div
                className="h-full bg-pine"
                style={{
                  width: thumbWidth,
                  x,
                }}
              />
            </div>

            {/* arrows */}
            <div className="flex items-center gap-3">
              <PaginationButton
                chevronDirection="left"
                disabled={isAtBeginningOfScroll}
                onClick={onPrevClick}
                variant="small"
              />
              <PaginationButton
                chevronDirection="right"
                disabled={isAtEndOfScroll}
                onClick={onNextClick}
                variant="small"
              />
            </div>
          </div>
        </div>
      )}

      {matchingSchematic.content && (
        <div data-testid={selectors.schematics.richTextContainer}>
          <RichText richTextResponse={matchingSchematic.content} />
        </div>
      )}

      {matchingSchematic.specs && (
        <table data-testid={selectors.schematics.specsContainer}>
          <tbody className="space-y-1">
            {matchingSchematic.specs.map((spec) => (
              <tr key={spec.key}>
                <td className="leading-5 w-36">{spec.key}</td>
                <td className="text-charcoal">{spec.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Schematics({ selectedOptions, schematics }: Props) {
  const matchingSchematic = findFirstMatchingSchematic(
    schematics,
    selectedOptions
  );

  if (!matchingSchematic)
    return (
      <p data-testid={selectors.schematics.emptyState}>
        No matching schematic found
      </p>
    );

  return <SchematicsComponent matchingSchematic={matchingSchematic} />;
}
